import { memo, useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import '@/assets/styles/components/file-upload.styles.scss';
import { Choose, If, Otherwise, When } from 'vite-plugin-react-control-statements';
import clsx from 'clsx';

import ExelIcon from '@/assets/svg/excel.svg';
import WordIcon from '@/assets/svg/word.svg';
import PowerpointIcon from '@/assets/svg/powerpoint.svg';
import PdfIcon from '@/assets/svg/pdf-file.svg';
import TxtIcon from '@/assets/svg/txt-file.svg';

import { getExtension, getFileName } from '@/utility';
import FileDownload from 'js-file-download';
import { isWebView, sendMessageToWebView } from '@/utility/webview.ts';
import { PostMessage } from '@/constants/post-message.ts';

interface FileUploadProps {
	label?: string;
	onChange?: (file: File | null) => void;
	accept?: string;
	currentFile?: {
		file: File;
		name: string;
		previewUrl?: string;
	};
	onRemove?: () => void;
	onShowPreview?: () => void;
	onPreload?: (isLoading: boolean) => void;
	buttonText?: string;
	containerClassName?: string;
	previewMode?: boolean;
	iconThumb?: boolean;
	isDownload?: boolean;
}

interface PreviewState {
	url: string;
	type: 'image' | 'pdf' | 'word' | 'other' | 'txt';
	mineType?: string;
}

export const FileUpload = memo(
	({ label, onChange, accept = '.pdf,.doc,.docx,image/*', currentFile, onRemove, buttonText, containerClassName, onShowPreview, previewMode, iconThumb, isDownload, onPreload }: FileUploadProps) => {
		const [preview, setPreview] = useState<PreviewState | null>(null);

		useEffect(() => {
			if (currentFile?.file) {
				const type = currentFile.file.type.startsWith('image/') ? 'image' : currentFile.file.type === 'application/pdf' ? 'pdf' : currentFile.file.type === 'text/plain' ? 'txt' : 'other';

				if (currentFile?.previewUrl) {
					setPreview({ url: currentFile.previewUrl, type });
				} else {
					generatePreview(currentFile.file);
				}
			} else if (currentFile?.previewUrl) {
				const extension = getExtension(currentFile.previewUrl);

				const type = extension === 'png' || extension === 'jpg' || extension === 'jpeg' ? 'image' : extension === 'pdf' ? 'pdf' : extension === 'txt' ? 'txt' : 'other';

				setPreview({ url: currentFile.previewUrl, type });
			}

			return () => {
				if (preview?.url) {
					URL.revokeObjectURL(preview.url);
				}
			};
		}, [currentFile]);

		const generatePreview = (file: File) => {
			const url = URL.createObjectURL(file);

			if (file.type.startsWith('image/')) {
				setPreview({ url, type: 'image', mineType: file.type });
			} else if (file.type === 'application/pdf') {
				setPreview({ url, type: 'pdf', mineType: file.type });
			}
			// else if (file.type === 'application/pdf') {
			// 	setPreview({ url, type: 'pdf', mineType: file.type });
			// }
			else {
				setPreview({ url, type: 'other', mineType: file.type });
			}
		};

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (file) {
				onChange?.(file);
			}
		};

		const onDownloadHandler = () => {
			if (preview) {
				if (isWebView()) {
					sendMessageToWebView({
						type: PostMessage.openExternalLink,
						data: {
							link: preview.url
						}
					});
				} else {
					onPreload?.(true);

					fetch(preview.url).then(response => {
						response
							.blob()
							.then(blob => {
								FileDownload(blob, getFileName(preview.url)!);
							})
							.finally(() => {
								onPreload?.(false);
							});
					});
				}
			}
		};

		const getIconByExtension = () => {
			const extension = getExtension(currentFile?.previewUrl || currentFile?.name);

			switch (extension?.toLowerCase()) {
				case 'doc':
				case 'docx':
					return WordIcon;
				case 'xls':
				case 'xlsx':
					return ExelIcon;
				case 'ppt':
				case 'pptx':
					return PowerpointIcon;
				default:
					return undefined;
			}
		};

		if (currentFile && preview) {
			return (
				<div className={clsx('file-upload', containerClassName)}>
					<div className="upload-area has-file">
						<If condition={!!onShowPreview || !!previewMode}>
							<a
								className="show-review-button"
								onClick={onShowPreview}>
								<div className="button-content">
									<i className="icons-magnifier" />
									<span className="review-text">Aç</span>
								</div>
							</a>
						</If>
						<If condition={!!isDownload}>
							<a
								className="download-button"
								onClick={onDownloadHandler}>
								<i className="icons-download" />
							</a>
						</If>
						<div className="preview-content">
							<Choose>
								<When condition={preview.type === 'image'}>
									<img
										src={preview.url as string}
										alt="Preview"
										className="preview-image"
										draggable={false}
									/>
								</When>
								<When condition={preview.type === 'pdf'}>
									{iconThumb ? (
										<img
											alt=""
											src={PdfIcon}
											className="thumb-icon"
										/>
									) : (
										<iframe
											src={`${process.env.DOPI_TECH_COMPONENTS_URL}/pdfjs/web/viewer.html?file=${encodeURIComponent(preview.url)}&hideButton=true&studentId=&userName=`}
											className="preview-document"
										/>
									)}
								</When>
								<When condition={preview.type === 'txt'}>
									{iconThumb ? (
										<img
											alt=""
											src={TxtIcon}
											className="thumb-icon"
										/>
									) : (
										<iframe
											src={preview.url}
											className="preview-document"
										/>
									)}
								</When>
								<Otherwise>
									{iconThumb ? (
										<img
											alt=""
											src={getIconByExtension()}
											className="thumb-icon"
										/>
									) : (
										<iframe
											src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(preview.url)}`}
											className="preview-document"
										/>
									)}
								</Otherwise>
							</Choose>
						</div>
						<If condition={!previewMode}>
							<button
								type="button"
								className="remove-button"
								onClick={onRemove}
								aria-label="Remove file">
								<Trash2 size={20} />
							</button>
						</If>
					</div>
				</div>
			);
		}

		return (
			<div className={clsx('file-upload', containerClassName)}>
				<div className="upload-area">
					<i className="icons-file-send" />
					<If condition={!!label}>
						<p className="upload-text">{label}</p>
					</If>
					<If condition={!!label}>
						<button className="upload-button">{buttonText}</button>
					</If>
					<input
						type="file"
						onChange={handleChange}
						accept={accept}
						className="file-input"
					/>
				</div>
			</div>
		);
	}
);

FileUpload.displayName = 'FileUpload';
